import moment from "moment";
import { DateFormatForBackend } from "../constants/date-picker-constant";
import { TimeFormatWithSeconds } from "../constants/time-picker-constant";
import { IShiftSchedule } from "../models/response/IShiftManagementResponse";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { formatMessage } from "../translations/format-message";
import { IOption } from "../models/ICommon";
import { DATE_FORMAT, MONTH_YEAR_FORMAT } from "../constants/constants";
import "moment/locale/ja";

dayjs.extend(isSameOrBefore);

export const getFormattedTime = (time: string) => {
    return moment(time, TimeFormatWithSeconds).format(TimeFormatWithSeconds);
}

export const getFormattedDateForBackend = (date: Date) => {
    return moment(date).format(DateFormatForBackend);
}

export const isDateInPublishedShiftRange = (
  publishedShiftsRange: {
    initialDateObj: Date | null;
    lastDateObj: Date | null;
  },
  dateToCheck: Date
) => {
  const { initialDateObj, lastDateObj } = publishedShiftsRange;

  if (!initialDateObj || !lastDateObj) return false;

  const checkDate = new Date(dateToCheck);
  checkDate.setHours(0, 0, 0, 0);

  const start = new Date(initialDateObj);
  start.setHours(0, 0, 0, 0);

  const end = new Date(lastDateObj);
  end.setHours(0, 0, 0, 0);

  return checkDate <= end;
}

export function getInitialAndLastDates(
    data: IShiftSchedule[],
    getClosedDays: { WeekDays: number; IsClosed: boolean; }[],
    targetMonth: Date
) {
    if (!data || data.length === 0) return {
        initialDate: 0, lastDate: 0, initialDateObj: null,
        lastDateObj: null,
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get target month's last day
    const targetMonthLastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);

    // Get published dates for target month only
    const dates = data
        .filter(item => {
            const itemDate = new Date(item.Date);
            return Number(item.ScheduleType) === 1 &&
                new Date(itemDate.setHours(0, 0, 0, 0)) >= today &&
                itemDate.getMonth() === targetMonth.getMonth() &&
                itemDate.getFullYear() === targetMonth.getFullYear();
        })
        .map(item => new Date(item.Date));

    if (dates.length === 0) return {
        initialDate: 0, lastDate: 0, initialDateObj: null,
        lastDateObj: null,
    };

    // Get initial and last dates from published schedules
    let initialDateObj = new Date(Math.min(...dates.map(date => date.getTime())));
    let lastDateObj = new Date(Math.max(...dates.map(date => date.getTime())));

    // Get closed weekdays
    const closedWeekDays = getClosedDays
        .filter(day => day.IsClosed)
        .map(day => day.WeekDays);

    // Check consecutive closed days before the initial date
    let currentDateBefore = new Date(initialDateObj);
    let consecutiveClosedDaysBefore = true;

    while (consecutiveClosedDaysBefore) {
        currentDateBefore.setDate(currentDateBefore.getDate() - 1);

        // Stop if we go before target month or today
        if (currentDateBefore.getMonth() !== targetMonth.getMonth() ||
            currentDateBefore < today) {
            break;
        }

        if (closedWeekDays.includes(currentDateBefore.getDay())) {
            initialDateObj = new Date(currentDateBefore);
        } else {
            consecutiveClosedDaysBefore = false;
        }
    }

    // Check consecutive closed days after the last date
    let currentDateAfter = new Date(lastDateObj);
    let consecutiveClosedDaysAfter = true;

    while (consecutiveClosedDaysAfter) {
        currentDateAfter.setDate(currentDateAfter.getDate() + 1);

        // Stop if we reach next month
        if (currentDateAfter > targetMonthLastDay) {
            break;
        }

        if (closedWeekDays.includes(currentDateAfter.getDay())) {
            lastDateObj = new Date(currentDateAfter);
        } else {
            consecutiveClosedDaysAfter = false;
        }
    }

    const initialDay = initialDateObj.getDate();
    const lastDay = lastDateObj.getDate();

    return {
        initialDate: initialDay,
        lastDate: lastDay,
        initialDateObj: initialDateObj,
        lastDateObj: lastDateObj
    };
}


export function getIsCellDisabled(initialDate: Date | null, lastDate: Date | null, dateToCheck: Date): boolean {
    if (!initialDate || !lastDate || !dateToCheck) return false;
    // Normalize all dates to remove time part for date-only comparison
    const normalize = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const start = normalize(initialDate).getDate();
    const end = normalize(lastDate).getDate();
    const target = normalize(dateToCheck).getDate();
    return target >= start && target <= end;
}

export function getDateRange(startDate: string, endDate: string): string[] {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const dates: string[] = [];

    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
        return dates; // Return empty array for invalid or reversed range
    }

    let current = start;
    while (current.isSameOrBefore(end, "day")) {
        dates.push(current.format("YYYY-MM-DD"));
        current = current.add(1, "day");
    }

    return dates;
}

export const formatTimeDisplay = (minutes: number): string => {
    if (!minutes) return `0 ${formatMessage("mins")}`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
        return `${minutes} ${formatMessage("mins")}`;
    } else if (remainingMinutes === 0) {
        return `${hours} ${formatMessage(hours > 1 ? 'hrs' : 'hr')}`;
    } else {
        return `${hours} ${formatMessage(hours > 1 ? 'hrs' : 'hr')} ${remainingMinutes} ${formatMessage("mins")}`;
    }
};

export function getDaysArrayForMonth(year: number, month: number): IOption[] {
    const daysCount = new Date(year, month, 0).getDate();
    return [{ id: -1, value: "dob_date" }, ...Array.from({ length: daysCount }, (_, i) => {
        let data = i + 1; return { id: data, value: String(data) }
    })];
}

export function getYearsArray(range: number = 101): IOption[] {
    const currentYear = new Date().getFullYear();
    return [{ id: -1, value: "year" }, ...Array.from({ length: range }, (_, i) => {
        let data = currentYear - i;
        return { id: data, value: String(data) };
    })];
}
export function getMonthsArray(range: number = 12): IOption[] {
    return [{ id: -1, value: "month" }, ...Array.from({ length: range }, (_, i) => {
        let data = i + 1;
        return { id: data, value: String(data) };
    })];
}
// Format Date Lable for Dashboard Weekly Reservation Status
export function formatDateLabel(dateString: string) {
  const date = new Date(dateString);
  const enDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
//   return `${date.getMonth() + 1}/${date.getDate()}(${formatMessage(enDays[date.getDay()])})`;
return {
    date: `${date.getMonth() + 1}/${date.getDate()}`,
    day: enDays[date.getDay()]
  };
}

export const getEffectiveDateTime = () =>{
  return moment(new Date()).format(DATE_FORMAT)
}

export const getEffectiveMonthYear = () =>{
  return moment(new Date()).format(MONTH_YEAR_FORMAT)
}

export const formatBookingDate = (date: string | Date, lang: string) => {
  if (lang === "jp") {
    // Japanese: 2025年5月21日
    return moment(date).locale("ja").format("YYYY年M月D日");
  } else {
    // English: 2025-05-21
    return moment(date).locale("en").format("YYYY-MM-DD");
  }
};
